// Integration Flows tab — in-app reference for how each UI action maps to the
// BFF and onward to the Keycloak Admin API. Static (no live endpoint).
import { Box, Typography, useTheme } from "@mui/material";
import { tokens } from "../../../theme";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { MONO_FONT, BFF_ROUTE_MAP } from "../data/mockUsersRoles";

const TAB_COLOR = {
  Members: "#1B365D",
  Invite: "#16a34a",
  Roles: "#0087B4",
  Activity: "#7c3aed",
};

const IntegrationFlowsTab = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Group rows by tab for readable sectioning
  const grouped = BFF_ROUTE_MAP.reduce((acc, row) => {
    (acc[row.tab] = acc[row.tab] || []).push(row);
    return acc;
  }, {});

  return (
    <Box>
      {/* Architecture rule banner */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          alignItems: "flex-start",
          backgroundColor: "rgba(27,54,93,0.04)",
          border: `1px solid ${theme.palette.divider}`,
          borderLeft: `3px solid ${colors.primary[500]}`,
          borderRadius: 2,
          p: 1.75,
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.25,
            backgroundColor: "rgba(27,54,93,0.08)",
            color: 'var(--fx-text-primary)',
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, color: 'var(--fx-text-primary)' }}>
            The browser never calls the Keycloak Admin API directly
          </Typography>
          <Typography sx={{ fontSize: "0.6875rem", color: colors.grey[500], mt: 0.3, lineHeight: 1.55 }}>
            Every action below is proxied through a BFF route handler under{" "}
            <Box component="span" sx={{ fontFamily: MONO_FONT, color: colors.secondary[500] }}>/api/users/*</Box>.
            The BFF authorizes the caller, mints a service-account token via the{" "}
            <Box component="span" sx={{ fontFamily: MONO_FONT, color: colors.secondary[500] }}>client_credentials</Box>{" "}
            grant on the{" "}
            <Box component="span" sx={{ fontFamily: MONO_FONT, color: colors.secondary[500] }}>pipeline-api-admin</Box>{" "}
            confidential client, then calls Keycloak server-to-server.
          </Typography>
        </Box>
      </Box>

      {/* Route map grouped by tab */}
      {Object.entries(grouped).map(([tab, rows]) => (
        <Box key={tab} sx={{ mb: 2 }}>
          <Typography
            sx={{
              fontFamily: MONO_FONT,
              fontSize: "0.625rem",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: TAB_COLOR[tab] || colors.grey[500],
              mb: 0.75,
            }}
          >
            {tab}
          </Typography>

          <Box sx={{ backgroundColor: "var(--fx-paper)", border: `1px solid ${theme.palette.divider}`, borderRadius: 2, overflow: "hidden" }}>
            {rows.map((row, i) => (
              <Box
                key={`${row.action}-${i}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "200px 1fr 24px 1fr" },
                  alignItems: "center",
                  gap: 1,
                  px: 1.75,
                  py: 1.1,
                  borderBottom: i === rows.length - 1 ? "none" : `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: 'var(--fx-text-primary)' }}>
                  {row.action}
                </Typography>
                <Box
                  component="code"
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: "0.625rem",
                    color: colors.secondary[600],
                    backgroundColor: "rgba(0,135,180,0.06)",
                    border: "1px solid rgba(0,135,180,0.15)",
                    borderRadius: 1,
                    px: 0.75,
                    py: 0.4,
                    justifySelf: "start",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                  }}
                >
                  {row.bff}
                </Box>
                <ArrowRightAltIcon sx={{ fontSize: 18, color: colors.grey[500], display: { xs: "none", md: "block" } }} />
                <Box
                  component="code"
                  sx={{
                    fontFamily: MONO_FONT,
                    fontSize: "0.625rem",
                    color: colors.grey[500],
                    justifySelf: "start",
                    minWidth: 0,
                    maxWidth: "100%",
                    overflowWrap: "anywhere",
                  }}
                >
                  {row.kc}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      ))}

      <Typography sx={{ fontSize: "0.625rem", color: colors.grey[500], mt: 1 }}>
        Full reference: <Box component="span" sx={{ fontFamily: MONO_FONT }}>docs/users-roles-api.md</Box>
      </Typography>
    </Box>
  );
};

export default IntegrationFlowsTab;
